import { ReportData, ReportTemplate } from '@/types/report';
import { INITIAL_FORM_DATA } from '../constants';
import type { ReportCover } from '@/types/cover';

export interface CreateReportLocationState {
    pendingTemplateId?: string;
    preSelectedPatient?: ReportData['patient'];
}

export interface TemplatePreviewLocationState {
    returnTo: {
        pathname: string;
        search?: string;
        state?: CreateReportLocationState;
    };
}

interface BuildReportFromTemplateOptions {
    patient?: ReportData['patient'];
    id?: string;
    created_at?: string;
    updated_at?: string;
    status?: ReportData['status'];
    /**
     * Templates never carry covers (the API strips them); re-attach the
     * laudo's auto-applied cover so picking a template doesn't drop it.
     */
    cover?: ReportCover;
}

export const buildReportDataFromTemplate = (
    template: ReportTemplate,
    options: BuildReportFromTemplateOptions = {},
): ReportData => {
    const sanitizedTemplate: ReportTemplate = {
        ...template,
        tests: template.tests
            ? {
                  ...template.tests,
                  blocks: template.tests.blocks?.map((block) => ({
                      ...block,
                      automatic: false,
                      id:
                          block.id ||
                          `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  })),
              }
            : template.tests,
    };

    const mappedTemplate: Partial<ReportData> = {
        ...sanitizedTemplate,
        diagnosis_details: sanitizedTemplate.diagnosis?.important_details,
        selected_cids: sanitizedTemplate.diagnosis?.cid?.map((cid) => cid.code),
        customizations: {
            ...sanitizedTemplate.customizations,
            template: {
                name: sanitizedTemplate.name,
            },
        },
    };

    return {
        ...(INITIAL_FORM_DATA as ReportData),
        ...mappedTemplate,
        customizations: {
            ...INITIAL_FORM_DATA.customizations,
            ...mappedTemplate.customizations,
            ...(options.cover ? { cover: options.cover } : {}),
        },
        patient: options.patient,
        id: options.id,
        created_at: options.created_at,
        updated_at: options.updated_at,
        status: options.status || 'in_progress',
    };
};

const teaserContent = (title: string, excerpt?: string) => {
    if (excerpt?.trim()) {
        return `<p>${excerpt.trim()}</p><p>Conteúdo completo disponível ao desbloquear este modelo.</p>`;
    }

    return `<p>${title} estruturado no modelo completo.</p>`;
};

export const buildReportDataFromTemplatePreview = (template: ReportTemplate): ReportData => {
    const previewSections = template.preview?.sections ?? [];
    const mappedTemplate: Partial<ReportData> = {};

    for (const section of previewSections) {
        const content = teaserContent(section.title, section.excerpt);

        switch (section.key) {
            case 'demand_description':
                mappedTemplate.demand_description = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'procedures':
                mappedTemplate.procedures = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'behavior_observations':
                mappedTemplate.behavior_observations = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'clinical_history':
                mappedTemplate.clinical_history = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'diagnosis':
                mappedTemplate.diagnosis = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'recommendations':
                mappedTemplate.recommendations = {
                    section_title: section.title,
                    content,
                };
                break;
            case 'tests':
                mappedTemplate.tests = {
                    section_title: section.title,
                    blocks: [
                        {
                            block_type: 'text',
                            order: 0,
                            has_content: true,
                            content,
                            automatic: false,
                        },
                    ],
                };
                break;
            default:
                break;
        }
    }

    return {
        ...(INITIAL_FORM_DATA as ReportData),
        ...mappedTemplate,
        customizations: {
            ...INITIAL_FORM_DATA.customizations,
            ...template.preview?.customizations,
            template: {
                name: template.name,
            },
        },
        status: 'in_progress',
    };
};

export const clearPendingTemplateState = (
    state?: CreateReportLocationState,
): CreateReportLocationState | undefined => {
    if (!state) return undefined;

    const { pendingTemplateId, ...rest } = state;

    if (!pendingTemplateId) {
        return state;
    }

    return Object.keys(rest).length > 0 ? rest : undefined;
};
